import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

import hospitalUserHttpClient from "api/hospitalUserHttpClient";
import { useTranslation } from "react-i18next";

function HospitalAdminUserBasic() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { scCode } = useParams();

  const [step, setStep] = useState(1);
  const [isResetFlow, setIsResetFlow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [hospitalCode, setHospitalCode] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const openErrorSB = (msg) => {
    setErrorMessage(msg);
    setErrorSB(true);
  };

  const closeErrorSB = () => setErrorSB(false);

  const handleNext = async () => {
    if (!hospitalCode) {
      return;
    }
    setLoading(true);
    try {
      const res = await hospitalUserHttpClient.get(
        `/api/hospital-auth/check-status/${scCode}/${hospitalCode}`
      );
      const { adminStatus, resetPassword } = res.data;
      const needsReset = adminStatus === "PASSWORD RESET REQUIRED" || resetPassword === "y";
      console.log(needsReset);
      setIsResetFlow(needsReset);
      setStep(2);
    } catch (err) {
      openErrorSB(err.response?.data?.message || "Hospital not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!email) {
      return;
    }
    setLoading(true);
    try {
      await hospitalUserHttpClient.post(`/api/hospital-auth/send-otp/${scCode}/${hospitalCode}`, {
        email,
      });
      setStep(3);
    } catch (err) {
      openErrorSB(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!password) {
      return;
    }
    setLoading(true);
    try {
      const response = await hospitalUserHttpClient.post(`/api/hospital-auth/login/${scCode}`, {
        hospitalCode,
        password,
      });
      localStorage.setItem("auth", JSON.stringify(response.data));
      navigate("/hospital/claims", { replace: true });
    } catch (err) {
      openErrorSB(err.response?.data?.message || "Invalid Credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async () => {
    if (newPassword !== confirmPassword) {
      openErrorSB("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await hospitalUserHttpClient.post(`/api/hospital-auth/reset-password/${scCode}`, {
        hospitalCode,
        otp,
        newPassword,
      });
      openErrorSB("Password updated! Please login with your new password.");
      setStep(1);
      setPassword("");
    } catch (err) {
      openErrorSB(err.response?.data?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // This stops the page refresh
    if (step === 1) {
      handleNext();
    } else if (step === 2) {
      if (isResetFlow) {
        handleSendOtp();
      } else {
        handleLogin();
      }
    } else {
      handleResetSubmit();
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {step === 3 ? "Reset Password" : t("auth.hospitalAignInTitle")}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox mb={1}>
            <MDTypography variant="gradient" fontWeight="regular" color="warning" mt={1}>
              {`SC Code: ${scCode}`}
            </MDTypography>
          </MDBox>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {step === 1 && (
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Hospital SC Code"
                  fullWidth
                  value={hospitalCode}
                  onChange={(e) => setHospitalCode(e.target.value.toUpperCase())}
                />
              </MDBox>
            )}

            {step === 2 && (
              <>
                <MDTypography variant="caption" color="text" mb={2} display="block">
                  Account: <strong>{hospitalCode}</strong>
                  <MDTypography
                    variant="caption"
                    color="info"
                    sx={{ cursor: "pointer", ml: 1, fontWeight: "bold" }}
                    onClick={() => setStep(1)}
                  >
                    (Change)
                  </MDTypography>
                </MDTypography>
                <MDBox mb={2}>
                  {isResetFlow ? (
                    <MDInput
                      type="email"
                      label="Verify Admin Email"
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  ) : (
                    <MDInput
                      type="password"
                      label={t("auth.passwordLabel")}
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  )}
                </MDBox>
              </>
            )}

            {step === 3 && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Enter OTP Code"
                    fullWidth
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="New Password"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Confirm New Password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </MDBox>
              </>
            )}

            {step < 3 && !isResetFlow && (
              <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth disabled={loading} type="submit">
                {loading ? (
                  <CircularProgress size={24} color="white" />
                ) : (
                  <>
                    {step === 1 && "Next"}
                    {step === 2 && (isResetFlow ? "Verify Email" : "Sign In")}
                    {step === 3 && "Update Password"}
                  </>
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <MDSnackbar
        color="error"
        icon="warning"
        title="Account Notification"
        content={errorMessage || ""}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
        dateTime={""}
      />
    </BasicLayout>
  );
}

export default HospitalAdminUserBasic;
